import { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Homepage banner carousel.
 *
 * On mobile (<640px) we want the FULL banner image visible — no crop, no
 * letter-box — exactly like baji.live. To do that, the slot's aspect-ratio
 * follows the *currently visible* slide's natural mobile-image aspect ratio,
 * and the image is rendered with `object-contain` on mobile (so it can
 * never be cropped, even briefly while the aspect updates).
 *
 * On desktop (≥1024px) we keep the original behaviour (focal-point aware
 * cover crop), driven by the first slide's natural desktop aspect.
 */

const FOCAL_TO_POSITION = {
  "top-left":     "left top",
  "top":          "center top",
  "top-right":    "right top",
  "left":         "left center",
  "center":       "center center",
  "right":        "right center",
  "bottom-left":  "left bottom",
  "bottom":       "center bottom",
  "bottom-right": "right bottom",
};

export const PromoCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState({});
  const [idx, setIdx] = useState(0);
  // Per-slide aspect ratios: arrays indexed by slide.
  const [mobileAspects, setMobileAspects] = useState({});
  const [desktopAspects, setDesktopAspects] = useState({});
  const probedRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    api
      .get("/banners")
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.items || [];
        const mapped = items
          .filter((b) => b.image_url || b.image || b.image_mobile)
          .map((b) => {
            const desktop = b.image_url || b.image || "";
            const mobile = b.image_mobile || "";
            return {
              image_url: desktop || mobile,
              image_mobile: mobile || desktop,
              has_mobile_creative: !!mobile,
              fit_mode: b.fit_mode === "contain" ? "contain" : "cover",
              focal_point: FOCAL_TO_POSITION[b.focal_point] ? b.focal_point : "center",
              bg_color: b.bg_color || "",
              link_url: b.link_url || b.link,
              title: b.title || "",
            };
          });
        setSlides(mapped);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const total = slides.length;
  const allFailed = total > 0 && Object.keys(failed).length >= total;

  useEffect(() => {
    if (total <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 4500);
    return () => clearInterval(t);
  }, [total]);

  // Probe natural aspect ratios of every slide (mobile + desktop).
  useEffect(() => {
    slides.forEach((s, i) => {
      if (probedRef.current.has(`m-${i}`)) return;
      probedRef.current.add(`m-${i}`);
      const im = new Image();
      im.onload = () => {
        if (im.naturalWidth && im.naturalHeight) {
          setMobileAspects((p) => ({ ...p, [i]: im.naturalWidth / im.naturalHeight }));
        }
      };
      im.src = s.image_mobile || s.image_url;

      probedRef.current.add(`d-${i}`);
      const id_ = new Image();
      id_.onload = () => {
        if (id_.naturalWidth && id_.naturalHeight) {
          setDesktopAspects((p) => ({ ...p, [i]: id_.naturalWidth / id_.naturalHeight }));
        }
      };
      id_.src = s.image_url;
    });
  }, [slides]);

  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  if (!loaded || total === 0 || allFailed) return null;

  // Slot height is driven by the FIRST slide's natural aspect ratio so the
  // height stays stable across slides (matches baji.live UX). Other slides
  // are shown with object-contain on mobile so they fit fully — never
  // cropped — and may letter-box slightly if their aspect differs.
  const baseMobileAspect = mobileAspects[0];
  const baseDesktopAspect = desktopAspects[0];

  const slotStyle = {
    "--m-aspect": baseMobileAspect ? `${baseMobileAspect}` : "16/9",
    "--d-aspect": baseDesktopAspect ? `${baseDesktopAspect}` : "16/5",
  };

  return (
    <div data-testid="promo-carousel" className="relative">
      <style>{`
        .promo-slot { aspect-ratio: var(--m-aspect, 16 / 9); }
        @media (min-width: 1024px) {
          .promo-slot { aspect-ratio: var(--d-aspect, 16 / 5); }
        }
      `}</style>
      <div
        className="promo-slot relative overflow-hidden rounded-xl sm:rounded-2xl bg-[#0a1119] max-h-[520px]"
        style={slotStyle}
      >
        <div
          className="flex h-full transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {slides.map((s, i) => {
            const isCover = s.fit_mode === "cover";
            const objectPosition = isCover
              ? FOCAL_TO_POSITION[s.focal_point] || "center center"
              : "center";

            const desktopSrc = s.image_url;
            const mobileSrc = s.image_mobile || s.image_url;

            const inner = (
              <div
                className="relative w-full h-full"
                style={s.bg_color ? { backgroundColor: s.bg_color } : undefined}
                data-testid={`promo-slide-${i}`}
              >
                {/* Foreground banner image.
                    - Mobile (<1024px): always object-contain → full image,
                      never cropped (slot already matches its aspect ratio,
                      so there's no letter-boxing either).
                    - Desktop (≥1024px): respects admin's fit_mode (cover by
                      default with focal-point cropping). */}
                <picture>
                  <source media="(max-width: 1023px)" srcSet={mobileSrc} />
                  <img
                    src={desktopSrc}
                    alt={s.title || `promotion ${i + 1}`}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className={`relative z-[1] w-full h-full object-contain ${
                      isCover ? "lg:object-cover" : "lg:object-contain"
                    } object-center`}
                    style={{ objectPosition }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      setFailed((f) => (f[i] ? f : { ...f, [i]: true }));
                    }}
                    draggable={false}
                  />
                </picture>

                {/* Subtle gradient at the bottom only on desktop cover mode. */}
                {isCover && (
                  <div className="hidden lg:block absolute inset-0 z-[2] bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
                )}
              </div>
            );
            return (
              <div key={i} className="min-w-full h-full relative">
                {s.link_url ? (
                  <a href={s.link_url} target="_blank" rel="noreferrer" className="block w-full h-full">
                    {inner}
                  </a>
                ) : (
                  inner
                )}
              </div>
            );
          })}
        </div>
      </div>

      {total > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="prev"
            data-testid="promo-prev"
            className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-11 sm:w-11 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white z-10"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={next}
            aria-label="next"
            data-testid="promo-next"
            className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-11 sm:w-11 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur flex items-center justify-center text-white z-10"
          >
            <ChevronRight size={18} />
          </button>

          <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2 z-10">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "bg-white w-5 sm:w-6" : "bg-white/40 w-1.5 hover:bg-white/60"
                }`}
                aria-label={`go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
